import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { formatMessage } from "@/lib/formatMessage";
import { NEWSLETTER_TEMPLATES } from "@/lib/newsletterTemplates";
import { FileText, Upload, Mail, Trash2, ExternalLink, Bold, Heading, Link2, X, Eye } from "lucide-react";
import { toast } from "sonner";

const PUBLIC = process.env.REACT_APP_BACKEND_URL;

export default function Marketing() {
  const [docs, setDocs] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [createForm, setCreateForm] = useState({ title: "", content: "" });
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [emailDoc, setEmailDoc] = useState(null);
  const [emailForm, setEmailForm] = useState({ audience: "all", subject: "", message: "" });
  const [audienceCount, setAudienceCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const contentRef = useRef(null);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/templates").then((r) => setCustomTemplates(r.data)).catch(() => {}); }, []);

  const docThumb = (d) => {
    if (d.kind !== "upload") return null;
    if ((d.content_type || "").startsWith("image/")) return `${PUBLIC}/api/documents/${d.id}/file`;
    if (d.thumb_path) return `${PUBLIC}/api/documents/${d.id}/thumb`;
    return null;
  };

  useEffect(() => {
    if (!emailDoc) return;
    setAudienceCount(null);
    api.get(`/admin/audience-count?audience=${emailForm.audience}`).then((r) => setAudienceCount(r.data.count)).catch(() => {});
  }, [emailDoc, emailForm.audience]);

  const insertFormat = (kind) => {
    const el = contentRef.current;
    const val = createForm.content;
    const start = el ? el.selectionStart : val.length;
    const end = el ? el.selectionEnd : val.length;
    const sel = val.slice(start, end);
    let snippet;
    if (kind === "bold") snippet = `**${sel || "bold text"}**`;
    else if (kind === "heading") snippet = `${start > 0 && val[start - 1] !== "\n" ? "\n" : ""}## ${sel || "Heading"}`;
    else snippet = `[${sel || "link text"}](https://)`;
    const next = val.slice(0, start) + snippet + val.slice(end);
    setCreateForm((f) => ({ ...f, content: next }));
    setTimeout(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = start + snippet.length; } }, 0);
  };

  const createDoc = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/documents", createForm);
      toast.success("Document created");
      setCreateForm({ title: "", content: "" }); setPreview(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create document"); }
    finally { setCreating(false); }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      await api.post("/admin/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not upload"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeDoc = async (id) => {
    try { await api.delete(`/admin/documents/${id}`); setDocs((d) => d.filter((x) => x.id !== id)); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  const sendDoc = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/documents/${emailDoc.id}/email`, emailForm);
      toast.success(`Sent to ${data.sent} ${data.audience}${data.failed ? ` (${data.failed} failed)` : ""}`);
      setEmailDoc(null); setEmailForm({ audience: "all", subject: "", message: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-bone px-6 md:px-12 py-16" data-testid="marketing-page">
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-rust">Marketing</span>
          <h1 className="font-serif font-light text-5xl md:text-7xl tracking-tight mt-4">Documents</h1>
          <p className="font-sans text-charcoal/60 mt-4 max-w-xl">Create newsletters and flyers, upload files, and email them to your community.</p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-6 mt-14">
          {/* CREATE */}
          <Reveal>
            <form onSubmit={createDoc} className="border border-charcoal/15 p-8 h-full" data-testid="create-document-form">
              <div className="flex items-center gap-3 mb-5"><FileText size={20} className="text-olive" strokeWidth={1.5} /><h2 className="font-serif text-2xl">Create a Document</h2></div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60">Title</label>
              <input value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} required data-testid="create-document-title"
                className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans focus:outline-none focus:border-olive" />
              <div className="flex flex-wrap gap-2 mt-4" data-testid="doc-templates">
                {NEWSLETTER_TEMPLATES.map((t) => (
                  <button key={t.id} type="button" onClick={() => { setCreateForm({ title: t.subject, content: t.message }); setPreview(false); }}
                    data-testid={`doc-template-${t.id}`} className="px-3 py-1.5 border border-charcoal/25 font-mono text-[10px] uppercase tracking-widest hover:bg-navy hover:text-bone transition-colors">
                    {t.label}
                  </button>
                ))}
                {customTemplates.map((t) => (
                  <button key={t.id} type="button" onClick={() => { setCreateForm({ title: t.subject, content: t.message }); setPreview(false); }}
                    data-testid={`doc-custom-template-${t.id}`} className="px-3 py-1.5 border border-olive/50 bg-olive/5 font-mono text-[10px] uppercase tracking-widest text-olive hover:bg-navy hover:text-bone transition-colors">
                    {t.name}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between mt-5">
                <label className="font-mono text-[10px] uppercase tracking-widest text-charcoal/60">Content</label>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => insertFormat("heading")} title="Heading" className="p-1.5 border border-charcoal/20 hover:bg-navy hover:text-bone transition-colors"><Heading size={13} /></button>
                  <button type="button" onClick={() => insertFormat("bold")} title="Bold" className="p-1.5 border border-charcoal/20 hover:bg-navy hover:text-bone transition-colors"><Bold size={13} /></button>
                  <button type="button" onClick={() => insertFormat("link")} title="Link" className="p-1.5 border border-charcoal/20 hover:bg-navy hover:text-bone transition-colors"><Link2 size={13} /></button>
                  <button type="button" onClick={() => setPreview((p) => !p)} data-testid="create-document-preview-toggle" title="Preview" className={`p-1.5 border border-charcoal/20 transition-colors ${preview ? "bg-olive text-bone" : "hover:bg-navy hover:text-bone"}`}><Eye size={13} /></button>
                </div>
              </div>
              {preview ? (
                <div className="w-full min-h-[160px] border border-charcoal/20 p-4 mt-1 bg-white font-sans text-sm text-charcoal/85" data-testid="create-document-preview" dangerouslySetInnerHTML={{ __html: formatMessage(createForm.content) || "<span style='color:#999'>Nothing to preview yet.</span>" }} />
              ) : (
                <textarea ref={contentRef} value={createForm.content} onChange={(e) => setCreateForm({ ...createForm, content: e.target.value })} required rows={7} data-testid="create-document-content"
                  className="w-full border border-charcoal/20 bg-transparent p-3 mt-1 font-sans text-sm focus:outline-none focus:border-olive resize-none" />
              )}
              <p className="font-sans text-[11px] text-charcoal/40 mt-1">Formatting: <span className="font-mono">## Heading</span>, <span className="font-mono">**bold**</span>, <span className="font-mono">[text](https://…)</span></p>
              <button disabled={creating} data-testid="create-document-save" className="mt-6 bg-charcoal text-bone px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">
                {creating ? "Saving…" : "Create Document"}
              </button>
            </form>
          </Reveal>

          {/* UPLOAD */}
          <Reveal delay={0.05}>
            <div className="border border-charcoal/15 p-8 h-full flex flex-col" data-testid="upload-document">
              <div className="flex items-center gap-3 mb-5"><Upload size={20} className="text-olive" strokeWidth={1.5} /><h2 className="font-serif text-2xl">Upload a File</h2></div>
              <p className="font-sans text-sm text-charcoal/60">PDF, images, Word, PowerPoint, or text — up to 10MB.</p>
              <label className="mt-6 flex-1 min-h-[180px] border-2 border-dashed border-charcoal/25 grid place-items-center cursor-pointer hover:border-olive transition-colors" data-testid="upload-dropzone">
                <div className="text-center">
                  <Upload size={28} className="mx-auto text-charcoal/40" strokeWidth={1.4} />
                  <p className="font-mono text-xs uppercase tracking-widest text-charcoal/60 mt-3">{uploading ? "Uploading…" : "Choose a file"}</p>
                </div>
                <input ref={fileRef} type="file" onChange={uploadDoc} disabled={uploading} data-testid="upload-input"
                  accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.ppt,.pptx,.txt,.csv" className="hidden" />
              </label>
            </div>
          </Reveal>
        </div>

        {/* LIBRARY */}
        <Reveal delay={0.1}>
          <div className="mt-14" data-testid="document-library">
            <h2 className="font-serif text-3xl mb-6">Document Library ({docs.length})</h2>
            <div className="border border-charcoal/10 divide-y divide-charcoal/10">
              {docs.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-4 px-5 py-4" data-testid={`doc-${d.id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    {docThumb(d) ? (
                      <img src={docThumb(d)} alt="" className="shrink-0 w-12 h-12 object-cover border border-charcoal/15 bg-sand" data-testid={`doc-thumb-${d.id}`} />
                    ) : (
                      <span className="shrink-0 w-12 h-12 grid place-items-center bg-sand text-charcoal">
                        {d.kind === "upload" ? <Upload size={16} /> : <FileText size={16} />}
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="font-serif text-lg truncate">{d.title}</p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40">
                        {d.kind === "upload" ? d.original_filename : "Created"}{d.email_count ? ` · emailed ${d.email_count}×` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a href={`${PUBLIC}/doc/${d.id}`} target="_blank" rel="noreferrer" data-testid={`doc-view-${d.id}`} title="View"
                      className="p-2 border border-charcoal/20 hover:bg-charcoal hover:text-bone transition-colors"><ExternalLink size={14} /></a>
                    <button onClick={() => { setEmailDoc(d); setEmailForm({ audience: "all", subject: d.title, message: "" }); }} data-testid={`doc-email-${d.id}`} title="Email"
                      className="p-2 border border-charcoal/20 hover:bg-olive hover:text-bone transition-colors"><Mail size={14} /></button>
                    <button onClick={() => removeDoc(d.id)} data-testid={`doc-delete-${d.id}`} title="Delete"
                      className="p-2 border border-charcoal/20 hover:bg-rust hover:text-bone transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
              {docs.length === 0 && <div className="px-5 py-10 font-sans text-charcoal/50">No documents yet. Create or upload one above.</div>}
            </div>
          </div>
        </Reveal>
      </div>

      {/* EMAIL DOC MODAL */}
      {emailDoc && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="email-document-modal">
          <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={() => !busy && setEmailDoc(null)} />
          <form onSubmit={sendDoc} className="relative bg-bone w-full max-w-md p-8 border border-charcoal/20">
            <button type="button" onClick={() => setEmailDoc(null)} className="absolute top-4 right-4 text-charcoal/40 hover:text-rust"><X size={18} /></button>
            <h3 className="font-serif text-3xl tracking-tight">Email Document</h3>
            <p className="font-sans text-sm text-charcoal/55 mt-1 truncate">{emailDoc.title}</p>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-6">Send To</label>
            <select value={emailForm.audience} onChange={(e) => setEmailForm({ ...emailForm, audience: e.target.value })} data-testid="email-document-audience"
              className="w-full border border-charcoal/20 bg-transparent px-3 py-2.5 mt-1 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-olive">
              <option value="all">Everyone</option>
              <option value="members">Members (non-admin)</option>
              <option value="supporters">Supporters only</option>
            </select>
            <p className="font-sans text-xs text-olive mt-2">{audienceCount === null ? "Counting recipients…" : `This will reach ${audienceCount} ${audienceCount === 1 ? "person" : "people"}.`}</p>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Subject</label>
            <input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} required data-testid="email-document-subject"
              className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans focus:outline-none focus:border-olive" />
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Short Intro <span className="text-charcoal/35 normal-case tracking-normal">(optional)</span></label>
            <textarea value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} rows={3} data-testid="email-document-message"
              className="w-full border border-charcoal/20 bg-transparent p-3 mt-1 font-sans text-sm focus:outline-none focus:border-olive resize-none" />
            <p className="font-sans text-[11px] text-charcoal/40 mt-1">A "View Document" button linking to the document will be added automatically.</p>
            <button disabled={busy} data-testid="email-document-send" className="mt-7 w-full bg-charcoal text-bone py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">
              {busy ? "Sending…" : "Send Document"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
